import ApiService from "@/api/ApiService";
import StyledModal from "@/components/planning/dialogs/newMaterialOrder/StyledModal";
import { KeyboardOptionKeyOutlined, Settings } from "@mui/icons-material";
import { CircularProgress, IconButton, Box, Select, FormControl, SelectChangeEvent, MenuItem } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import React, { useState } from "react";
import { Fragment } from "react/jsx-runtime";
const options = [
  { value: "silent", title: "Nur Startseite" },
  { value: "mail_only", title: "E-Mail" },
  { value: "app_only", title: "App-Benachrichtigung" },
  { value: "app_and_mail", title: "E-Mail und App-Benachrichtigung" },
] as const;

type NotificationValue = (typeof options)[number]["value"];

type NotiswitchProps = {
  type: string;
  value: NotificationValue;
};

type UpdateNotificationPayload = {
  type: string;
  value: NotificationValue;
};

export function Notiswitch({ type, value }: NotiswitchProps) {
  const queryClient = useQueryClient();

  // Lokaler State, damit das Select nicht zwischen altem Prop-Wert und neuem Wert hin- und herspringt
  const [localValue, setLocalValue] = React.useState<NotificationValue>(value);

  // Trackt, ob gerade eine lokale Änderung "führend" ist
  const [isDirty, setIsDirty] = React.useState(false);

  // Nur dann vom Parent synchronisieren, wenn wir nicht gerade selbst eine Änderung halten
  React.useEffect(() => {
    if (!isDirty) {
      setLocalValue(value);
    }
  }, [value, isDirty]);

  const changeMutation = useMutation<unknown, AxiosResponse, UpdateNotificationPayload>({
    mutationFn: ApiService.setNotificationSetting(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getNotificationSettings"],
      });
      // localValue bleibt bis zum frischen Serverwert erhalten
      setIsDirty(false);
    },
    onError: () => {
      // Bei Fehler auf Serverwert zurücksetzen
      setLocalValue(value);
      setIsDirty(false);
    },
  });

  const handleChange = (event: SelectChangeEvent<NotificationValue>) => {
    const newValue = event.target.value as NotificationValue;

    setLocalValue(newValue);
    setIsDirty(true);

    changeMutation.mutate({
      type,
      value: newValue,
    });
  };

  return (
    <FormControl sx={{ maxWidth: "300px" }} size="small" fullWidth>
      <Select
        value={localValue}
        onChange={handleChange}
        disabled={changeMutation.isPending}
        displayEmpty
        endAdornment={changeMutation.isPending ? <CircularProgress size={18} sx={{ mr: 1 }} /> : undefined}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.title}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function NotificationSettingContent({ onClose }) {
  const query = useQuery<any[], AxiosResponse>({
    queryKey: ["getNotificationSettings"],
    queryFn: ApiService.getNotificationSettings(),
  });
  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {query.isLoading ? (
        <CircularProgress sx={{ alignSelf: "center", m: 2 }} />
      ) : query.isError ? (
        "Fehler"
      ) : (
        query.data
          ?.sort((a, b) => a.title.localeCompare(b.title))
          .map((item) => (
            <Box
              sx={{
                p: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
                borderBottom: "1px solid #aaa",
                "&:last-child": { borderBottom: "none" },
              }}
              key={item.type}
            >
              <Box sx={{ flex: 1 }}>{item.title}</Box>
              <Notiswitch type={item.type} value={item.value} />
            </Box>
          ))
      )}
    </Box>
  );
}

export default function NotificationSettings() {
  const [open, setOpen] = useState(false);

  return (
    <Fragment>
      <IconButton onClick={() => setOpen(true)} size="small" sx={{ border: 1, borderColor: "divider", borderRadius: 1 }} aria-label="Öffnen">
        <Settings fontSize="inherit" />
      </IconButton>
      <StyledModal onShow={() => {}} open={open} onClose={() => setOpen(false)}>
        <NotificationSettingContent onClose={() => setOpen(false)} />
      </StyledModal>
    </Fragment>
  );
}
